import {
  CheckCheck, Contact, ExternalLink, Hash, LogOut, Lock, MessageCircleMore, MessageSquareOff, MessageSquareReply, Search, Send,
  SmilePlus, SquarePen, UserRound, Users, X,
} from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Input, Textarea } from "@/components/ui/input";
import { Avatar, Tooltip } from "@/components/ui/misc";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/toast";
import { CloserAskCard } from "@/features/closer-ask";
import { ChatPersonCard } from "@/features/chat-person-card";
import { PictureAvatar } from "@/features/user-avatar";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  closable, insertMention, type Mention, mentionIdsIn, mentionMatches, mentionParts, mentionQuery, messageable,
  messageRows, MORE_REACTIONS, newClientId, previewText, QUICK_REACTIONS, reactionWho, seenBy, seenText, threadLine,
} from "@/lib/chats";
import {
  CHATS_POLL, OTHER_TEAM_POLL, useChat, useChatMessages, useChatPeople, useChats, useChatThread, useCloseChat,
  useContacts, useJoinTeamChat, useLeaveChat, useMarkChatRead, useMarkThreadRead, useOpenDirectChat, useOpenTeamChats, useReaction, useReopenChat,
  useSendChatMessage,
} from "@/lib/queries";
import { catalogRow } from "@/lib/stages";
import type { ChatDetail, ChatMessage, ChatPerson, ChatSummary, ContactRow, SharedContact } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";

/**
 * Team Chat (platform ADR-186): the chats of the team the person is in, the conversations about the
 * contacts they handed off or took (platform ADR-189: the setter and the closer of each handoff,
 * named after the contact, with the ask's card and what they said after it), and direct messages
 * with anyone in their tenant. A team that is open to everyone (platform ADR-190) is found under
 * New message and joined as a guest, who reads and writes its chat, takes no ask there, and may
 * leave it. An admin also sees every other team's chat (platform ADR-207), under "Other teams":
 * they read and write there, and nothing in them is unread for them. What is said is kept; it does
 * not go when a call ends or somebody signs out. A contact can be shared into a chat as a card that
 * opens their record, for a reader who may open it.
 *
 * A direct chat or a contact's conversation can be closed (platform ADR-186, "Closing a chat"): it
 * leaves the person's list, nothing is deleted, and it comes back when someone writes in it, or
 * when it is opened again, by Message on a person or a link to it. Anyone in a chat may put emoji on
 * its messages ("Reactions"), as in Slack.
 *
 * `/messages/:id` opens one chat. It is read as it is opened and as messages arrive while it is
 * open, and a `chats` hint on the person's own live channel brings new ones without a reload.
 */
export function MessagesPage() {
  const { id } = useParams();
  const openId = id ? Number(id) : null;
  const navigate = useNavigate();
  const toast = useToast();
  // An admin reading another team's chat gets no hint for it (platform ADR-207): the list is read
  // again as often as the chat, so its place and last line keep up.
  const chats = useChats({
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.id === openId && c.other_team) ? OTHER_TEAM_POLL : CHATS_POLL,
  });
  const close = useCloseChat();
  const [search, setSearch] = React.useState("");
  const [starting, setStarting] = React.useState(false);

  const shown = (chats.data ?? []).filter((c) => c.title.toLowerCase().includes(search.trim().toLowerCase()));
  const teams = shown.filter((c) => c.kind === "team" && !c.other_team);
  const otherTeams = shown.filter((c) => c.kind === "team" && c.other_team);
  const contacts = shown.filter((c) => c.kind === "contact");
  const direct = shown.filter((c) => c.kind === "direct");
  const open = chats.data?.find((c) => c.id === openId) ?? null;
  // A chat not on the list, opened by a link to it (the record's "Answer in Team Chat", a bell): one
  // the person closed is theirs to read, and comes back on their list as it opens.
  const missing = openId !== null && chats.isSuccess && open === null;
  const linked = useChat(missing ? openId : null);
  const reopenMutate = useReopenChat().mutate;
  const reopened = React.useRef(new Set<number>());
  React.useEffect(() => {
    if (!missing || !linked.data || openId === null || reopened.current.has(openId)) return;
    reopened.current.add(openId);
    reopenMutate(openId);
  }, [missing, linked.data, openId, reopenMutate]);

  const closeChat = async (chat: ChatSummary) => {
    try {
      await close.mutateAsync(chat.id);
      if (chat.id === openId) navigate("/messages");
    } catch (e) {
      toast.error("Could not close the chat", e instanceof ApiError ? e.message : "Please try again.");
    }
  };
  const row = (c: ChatSummary) => (
    <ChatRow
      key={c.id}
      chat={c}
      selected={c.id === openId}
      onOpen={() => navigate(`/messages/${c.id}`)}
      onClose={closable(c) ? () => void closeChat(c) : undefined}
    />
  );

  return (
    <div className="flex h-full min-h-[34rem] flex-col">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border bg-surface px-4 py-2.5">
        <h1 className="text-lg font-semibold tracking-tight text-text">Team Chat</h1>
        <span className="flex-1" />
        <Button variant="default" size="sm" onClick={() => setStarting(true)}>
          <SquarePen />
          New message
        </Button>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden bg-surface">
        <aside className={cn("flex w-full shrink-0 flex-col border-r border-border lg:w-80", openId !== null && "hidden lg:flex")}>
          <div className="p-3">
            <Input
              leading={<Search className="size-4" />}
              placeholder="Find a team, a contact or a person"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Find a chat"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-subtle">
            {chats.isPending ? (
              <div className="flex justify-center p-6" role="status">
                <Spinner className="size-5" />
                <span className="sr-only">Loading chats</span>
              </div>
            ) : chats.error ? (
              <div className="p-3">
                <ErrorState error={chats.error} title="Could not load your chats" />
              </div>
            ) : (
              <>
                <ListHeading>Teams</ListHeading>
                {teams.length === 0 && <p className="px-4 pb-2 text-xs text-text-subtle">You are in no team yet.</p>}
                {teams.map(row)}
                {contacts.length > 0 && (
                  <>
                    <ListHeading>Contacts</ListHeading>
                    {contacts.map(row)}
                  </>
                )}
                <ListHeading>Direct messages</ListHeading>
                {direct.length === 0 && (
                  <p className="px-4 pb-2 text-xs text-text-subtle">
                    None yet. <button type="button" className="text-accent-11 hover:underline" onClick={() => setStarting(true)}>Message somebody</button>.
                  </p>
                )}
                {direct.map(row)}
                {otherTeams.length > 0 && (
                  <>
                    <ListHeading>Other teams</ListHeading>
                    {otherTeams.map(row)}
                  </>
                )}
              </>
            )}
          </div>
        </aside>

        {openId !== null ? (
          open || chats.isPending || linked.data || (missing && linked.isPending) ? (
            <Thread
              key={openId}
              chatId={openId}
              summary={open}
              onBack={() => navigate("/messages")}
              onClose={open && closable(open) ? () => void closeChat(open) : undefined}
            />
          ) : (
            <EmptyState
              className="flex-1 bg-surface-sunken"
              icon={MessageCircleMore}
              title="Not one of your chats"
              description="It may be a team you have left. Pick a chat on the left."
            />
          )
        ) : (
          <EmptyState
            className="hidden flex-1 bg-surface-sunken lg:flex"
            icon={MessageCircleMore}
            title="Pick a chat"
            description="Your teams' chats, the conversations about contacts you handed off or took, and your direct messages are on the left. What is said here is kept."
          />
        )}
      </div>

      <NewMessageDialog open={starting} onOpenChange={setStarting} onOpened={(chat) => navigate(`/messages/${chat.id}`)} />
    </div>
  );
}

function ListHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-4 pb-1.5 pt-3 text-2xs font-medium uppercase tracking-wide text-text-subtle">{children}</p>
  );
}

export function listTime(iso: string | undefined): string {
  if (!iso) return "";
  const at = new Date(iso);
  const now = new Date();
  if (at.toDateString() === now.toDateString()) return at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (now.getTime() - at.getTime() < 6 * 86_400_000) return at.toLocaleDateString(undefined, { weekday: "short" });
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function ChatIcon({ chat, size = "size-8" }: { chat: ChatSummary; size?: string }) {
  if (chat.kind === "direct") return <PictureAvatar name={chat.title} url={chat.other?.avatar_url} className={size} />;
  if (chat.kind === "contact") {
    return (
      <span className={cn("flex shrink-0 items-center justify-center rounded-lg bg-accent-3 text-accent-11", size)}>
        <Contact className="size-4" aria-hidden />
      </span>
    );
  }
  return (
    <span className={cn("flex shrink-0 items-center justify-center rounded-lg bg-gray-3 text-text-muted", size)}>
      {chat.archived ? <Lock className="size-3.5" aria-hidden /> : <Hash className="size-4" aria-hidden />}
    </span>
  );
}

function ChatRow({
  chat, selected, onOpen, onClose,
}: {
  chat: ChatSummary;
  selected: boolean;
  onOpen: () => void;
  /** Close it (a direct chat or a contact's conversation): an × in place of its time, on hover. */
  onClose?: () => void;
}) {
  const { user } = useAuth();
  const unread = chat.unread > 0;
  return (
    <div className="group relative">
    <button
      type="button"
      onClick={onOpen}
      aria-current={selected || undefined}
      className={cn(
        "relative flex w-full items-start gap-2.5 border-b border-border px-3 py-2.5 text-left transition-colors",
        selected
          ? "bg-accent-2 before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-accent-9"
          : "hover:bg-surface-hover",
      )}
    >
      <ChatIcon chat={chat} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className={cn("min-w-0 flex-1 truncate text-sm", unread ? "font-semibold text-text" : "text-text")}>
            {chat.title}
          </span>
          <time
            className={cn(
              "shrink-0 text-2xs text-text-subtle",
              onClose && "group-focus-within:invisible group-hover:invisible",
            )}
          >
            {listTime(chat.last_message?.created_at)}
          </time>
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          {chat.archived && <Badge size="sm" tone="outline">Team deleted</Badge>}
          {chat.guest && <Badge size="sm" tone="outline">Guest</Badge>}
          <span className={cn("min-w-0 flex-1 truncate text-xs", unread ? "text-text" : "text-text-muted")}>
            {previewText(chat.last_message, user?.id ?? 0)}
          </span>
          {unread && (
            <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded bg-accent-9 px-1 text-2xs font-semibold text-white">
              <span className="sr-only">Unread: </span>
              {chat.unread}
            </span>
          )}
        </span>
      </span>
    </button>
    {onClose && <CloseChatButton title={chat.title} onClose={onClose} />}
    </div>
  );
}

/** The × of a chat in a list, over where its time is, while the pointer is on the row. */
export function CloseChatButton({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <button
      type="button"
      aria-label={`Close ${title}`}
      title="Close: off your list until someone writes in it"
      onClick={onClose}
      className={cn(
        "absolute right-2 top-2 flex size-5 items-center justify-center rounded text-text-muted",
        "opacity-0 transition-opacity hover:bg-gray-4 hover:text-text",
        "group-hover:opacity-100 focus-visible:opacity-100",
      )}
    >
      <X className="size-3.5" aria-hidden />
    </button>
  );
}

// --- one chat ------------------------------------------------------------------------------------

/** Whether the chat's people are shown beside it, as the person last left it, in this browser. */
const PEOPLE_SHOWN_KEY = "acl.messages.people";

function readPeopleShown(): boolean {
  try {
    return localStorage.getItem(PEOPLE_SHOWN_KEY) === "true";
  } catch {
    return false;
  }
}

function writePeopleShown(shown: boolean): void {
  try {
    localStorage.setItem(PEOPLE_SHOWN_KEY, String(shown));
  } catch {
    /* non-fatal: the people stay hidden after a reload */
  }
}

function Thread({
  chatId, summary, onBack, onClose,
}: {
  chatId: number;
  summary: ChatSummary | null;
  onBack: () => void;
  /** Close it, for a direct chat or a contact's conversation (platform ADR-186, "Closing a chat"). */
  onClose?: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  // A thread beside the chat (platform ADR-191), in the address so the bell's link opens it.
  const [params, setParams] = useSearchParams();
  const threadId = Number(params.get("thread")) || null;
  const openThread = (id: number | null) => setParams(id ? { thread: String(id) } : {});
  const me = user?.id ?? 0;
  const detail = useChat(chatId);
  const leave = useLeaveChat();
  // The people beside the chat: hidden until asked for, then as the person left them.
  const [peopleShown, setPeopleShown] = React.useState(readPeopleShown);
  const showPeople = (shown: boolean) => {
    setPeopleShown(shown);
    writePeopleShown(shown);
  };
  const messages = useChatMessages(chatId, summary?.other_team ?? false);
  const read = useMarkChatRead();
  const bottom = React.useRef<HTMLDivElement>(null);
  const chat: ChatSummary | ChatDetail | null = detail.data ?? summary;
  const newest = messages.data?.at(-1)?.id ?? null;

  // Read as it is opened, and as messages arrive while it is open.
  const readMutate = read.mutate;
  React.useEffect(() => {
    if (newest !== null && (summary?.unread ?? 0) > 0) readMutate({ chatId, messageId: newest });
  }, [chatId, newest, summary?.unread, readMutate]);
  React.useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [newest]);

  const rows = messageRows(messages.data ?? []);
  // "Seen" under the newest message while it is the reader's own: once somebody has answered, it
  // says nothing their answer does not.
  const last = messages.data?.at(-1);
  const seen = last && last.sender_id === me && chat ? seenText(seenBy(detail.data, me, last.id), chat.kind) : null;

  return (
    <>
      <section className="flex min-w-0 flex-1 flex-col bg-surface-sunken" aria-label={chat?.title ?? "Chat"}>
        <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2.5">
          <Button variant="ghost" size="icon-sm" className="lg:hidden" aria-label="Back to the chats" onClick={onBack}>
            <X />
          </Button>
          {chat && <ChatIcon chat={chat} />}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-text">{chat?.title ?? "…"}</p>
            <p className="truncate text-xs text-text-muted">{chat ? threadSubtitle(chat) : ""}</p>
          </div>
          {detail.data && (
            <Button
              variant="ghost"
              size="sm"
              className={cn("hidden lg:inline-flex", peopleShown && threadId === null && "bg-surface-hover text-text")}
              aria-pressed={peopleShown && threadId === null}
              aria-label={peopleShown && threadId === null ? "Hide the people" : "Show the people"}
              title={peopleShown && threadId === null ? "Hide the people" : "Show the people"}
              onClick={() => {
                if (threadId !== null) {
                  openThread(null);
                  showPeople(true);
                } else showPeople(!peopleShown);
              }}
            >
              <Users />
              {detail.data.members.length}
            </Button>
          )}
          {chat?.kind === "contact" && chat.contact?.may_open && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/contacts/${chat.contact.lead_id}`}>
                <ExternalLink />
                Open record
              </Link>
            </Button>
          )}
          {chat?.guest && (
            <Button
              variant="outline"
              size="sm"
              loading={leave.isPending}
              onClick={async () => {
                try {
                  await leave.mutateAsync(chatId);
                  toast.success(`You left ${chat.title}`, "Join it again from New message.");
                  onBack();
                } catch (e) {
                  toast.error("Could not leave the chat", e instanceof ApiError ? e.message : "Please try again.");
                }
              }}
            >
              <LogOut />
              Leave
            </Button>
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              title="Off your list until someone writes in it. Nothing is deleted."
              onClick={onClose}
            >
              <MessageSquareOff />
              Close
            </Button>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-5 py-4 scrollbar-subtle">
          {messages.isPending ? (
            <div className="flex justify-center p-6" role="status">
              <Spinner className="size-5" />
            </div>
          ) : messages.error ? (
            <ErrorState error={messages.error} title="Could not load the messages" />
          ) : rows.length === 0 ? (
            <p className="py-10 text-center text-sm text-text-muted">No messages yet. Say hello.</p>
          ) : (
            rows.map(({ message, day, startsRun }) => (
              <React.Fragment key={message.id}>
                {day && (
                  <div className="flex items-center gap-3 py-2 text-2xs text-text-subtle">
                    <span className="h-px flex-1 bg-border" />
                    {day}
                    <span className="h-px flex-1 bg-border" />
                  </div>
                )}
                <MessageItem
                  message={message}
                  startsRun={startsRun}
                  mine={message.sender_id === me}
                  people={detail.data?.members}
                  chatKind={chat?.kind}
                  onOpenThread={openThread}
                  readOnly={chat?.archived}
                />
              </React.Fragment>
            ))
          )}
          {seen && (
            <p className="flex items-center justify-end gap-1 pr-1 text-2xs text-text-subtle">
              <CheckCheck className="size-3" aria-hidden />
              {seen}
            </p>
          )}
          <div ref={bottom} />
        </div>

        {chat?.archived ? (
          <div className="border-t border-border bg-surface p-3 text-xs text-text-muted">
            This team was deleted. Its chat is kept to read, and takes no more messages.
          </div>
        ) : (
          <Composer
            chatId={chatId}
            placeholder={chat ? (chat.kind === "contact" ? `Message about ${chat.title}` : `Message ${chat.title}`) : "Message"}
          />
        )}
      </section>
      {threadId !== null ? (
        <aside
          aria-label="Thread"
          className="absolute inset-0 z-20 flex flex-col bg-surface-sunken lg:static lg:z-auto lg:w-96 lg:shrink-0 lg:border-l lg:border-border"
        >
          <div className="flex items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-text">Thread</p>
              <p className="truncate text-xs text-text-muted">{chat?.title ?? ""}</p>
            </div>
            <Button variant="ghost" size="icon-sm" aria-label="Close the thread" onClick={() => openThread(null)}>
              <X />
            </Button>
          </div>
          <ThreadView chatId={chatId} rootId={threadId} chat={chat} />
        </aside>
      ) : (
        detail.data && peopleShown && <PeoplePanel chat={detail.data} onHide={() => showPeople(false)} />
      )}
    </>
  );
}

/**
 * A thread (platform ADR-191): its first message, its replies, and a box to reply. Shared by the
 * Team Chat page's panel and the chat window beside the phone. Read as it is opened and as replies
 * come, for a follower.
 */
export function ThreadView({
  chatId, rootId, chat, onOpenChat,
}: {
  chatId: number;
  rootId: number;
  chat: ChatSummary | ChatDetail | null;
  /** Where a person's card opens a direct chat; the Team Chat page when not given. */
  onOpenChat?: (chatId: number) => void;
}) {
  const { user } = useAuth();
  const me = user?.id ?? 0;
  const thread = useChatThread(chatId, rootId, chat?.other_team ?? false);
  const detail = useChat(chatId);
  const read = useMarkThreadRead();
  const bottom = React.useRef<HTMLDivElement>(null);
  const newest = thread.data?.replies.at(-1)?.id ?? null;
  const unread = thread.data?.root.thread?.unread ?? 0;

  const readMutate = read.mutate;
  React.useEffect(() => {
    if (newest !== null && unread > 0) readMutate({ chatId, rootId, messageId: newest });
  }, [chatId, rootId, newest, unread, readMutate]);
  React.useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [newest]);

  const replies = thread.data?.replies ?? [];
  const people = detail.data?.members;
  return (
    <>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-4 py-3 scrollbar-subtle">
        {thread.isPending ? (
          <div className="flex justify-center p-6" role="status">
            <Spinner className="size-5" />
          </div>
        ) : thread.error ? (
          <ErrorState error={thread.error} title="Could not load the thread" />
        ) : thread.data ? (
          <>
            <MessageItem
              message={thread.data.root}
              startsRun
              mine={thread.data.root.sender_id === me}
              people={people}
              chatKind={chat?.kind}
              onOpenChat={onOpenChat}
              readOnly={chat?.archived}
            />
            <div className="flex items-center gap-3 py-2 text-2xs text-text-subtle">
              <span className="h-px flex-1 bg-border" />
              {replies.length === 0 ? "No replies yet" : `${replies.length} ${replies.length === 1 ? "reply" : "replies"}`}
              <span className="h-px flex-1 bg-border" />
            </div>
            {messageRows(replies).map(({ message, startsRun }) => (
              <MessageItem
                key={message.id}
                message={message}
                startsRun={startsRun}
                mine={message.sender_id === me}
                people={people}
                chatKind={chat?.kind}
                onOpenChat={onOpenChat}
                readOnly={chat?.archived}
              />
            ))}
          </>
        ) : null}
        <div ref={bottom} />
      </div>
      {chat?.archived ? (
        <p className="shrink-0 border-t border-border bg-surface p-3 text-xs text-text-muted">
          This team was deleted. Its chat is kept to read, and takes no more messages.
        </p>
      ) : (
        <Composer chatId={chatId} threadId={rootId} placeholder="Reply in the thread" />
      )}
    </>
  );
}

/** Under a chat's name: how many people a team's or a contact's has, or who the other person is. */
export function threadSubtitle(chat: ChatSummary): string {
  const people = `${chat.member_count} ${chat.member_count === 1 ? "person" : "people"}`;
  if (chat.kind === "team") {
    const you = chat.guest ? " · you are a guest" : chat.other_team ? " · you are not in this team" : "";
    return `${people}${chat.archived ? " · the team was deleted" : you}`;
  }
  if (chat.kind === "contact") return `${people} · its setters and closers`;
  return chat.other ? roleText(chat.other.role) + (chat.other.extension ? ` · ext. ${chat.other.extension}` : "") : "";
}

function roleText(role: string): string {
  return role === "superadmin" ? "Superadmin" : role.charAt(0).toUpperCase() + role.slice(1);
}

/** The person who sent a message, for their card: who they are in the chat when they still are. */
function senderOf(m: ChatMessage, people: readonly ChatPerson[] | undefined): ChatPerson | null {
  if (m.sender_id === null) return null;
  const member = people?.find((p) => p.user_id === m.sender_id);
  return {
    user_id: m.sender_id,
    name: m.sender_name,
    role: member?.role ?? m.sender_role ?? "rep",
    is_active: member?.is_active ?? true,
    extension: member?.extension ?? null,
    avatar_url: m.sender_avatar_url ?? member?.avatar_url ?? null,
  };
}

export function MessageItem({
  message: m, startsRun, mine, people, chatKind, onOpenChat, onOpenThread, readOnly = false,
}: {
  message: ChatMessage;
  startsRun: boolean;
  mine: boolean;
  /** The chat's people, for the sender's card. */
  people?: readonly ChatPerson[];
  chatKind?: ChatSummary["kind"];
  /** Where the sender's card opens a direct chat with them; the Team Chat page when not given. */
  onOpenChat?: (chatId: number) => void;
  /** Opens this message's thread (platform ADR-191); without it, no Reply in thread and no line. */
  onOpenThread?: (rootId: number) => void;
  /** A chat kept only to read (a deleted team's): its reactions are shown, none is added. */
  readOnly?: boolean;
}) {
  const { user } = useAuth();
  const me = user?.id ?? 0;
  const myName = user?.display_name?.trim() || user?.email || "You";
  const react = useReaction();
  const [picking, setPicking] = React.useState(false);
  const reactions = m.reactions ?? [];
  const mineOn = new Set(reactions.filter((r) => r.mine).map((r) => r.emoji));
  // Picking one the reader is already on takes it back, as a press on its chip does.
  const toggle = (emoji: string, on = !mineOn.has(emoji)) =>
    react.mutate({ chatId: m.chat_id, messageId: m.id, emoji, on, myName });
  const at = new Date(m.created_at);
  const sender = senderOf(m, people);
  const senderName = (
    <>
      {m.sender_name ?? "Somebody who left"}
      {mine && " (you)"}
    </>
  );
  const threadable = onOpenThread !== undefined && !m.thread_id;
  return (
    <div className={cn("group relative flex items-start gap-2.5", startsRun ? "pt-2.5" : "")}>
      {(threadable || !readOnly) && (
        <div
          className={cn(
            "absolute right-0 top-0 z-10 flex items-center rounded-md border border-border bg-surface shadow-xs",
            "opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100",
            picking && "opacity-100",
          )}
        >
          {!readOnly && (
            <ReactionPicker open={picking} onOpenChange={setPicking} onPick={(emoji) => toggle(emoji)}>
              <button
                type="button"
                aria-label="Add a reaction"
                title="Add a reaction"
                className="flex items-center rounded-md px-1.5 py-1 text-text-muted hover:text-text"
              >
                <SmilePlus className="size-3.5" aria-hidden />
              </button>
            </ReactionPicker>
          )}
          {threadable && (
            <button
              type="button"
              aria-label="Reply in thread"
              title="Reply in thread"
              onClick={() => onOpenThread(m.id)}
              className="flex items-center gap-1 rounded-md px-1.5 py-1 text-2xs text-text-muted hover:text-text"
            >
              <MessageSquareReply className="size-3.5" aria-hidden />
              Reply
            </button>
          )}
        </div>
      )}
      {startsRun ? (
        <PictureAvatar name={m.sender_name} url={m.sender_avatar_url} className="mt-0.5 size-8" />
      ) : (
        <span className="w-8 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        {startsRun && (
          <p className="flex items-baseline gap-2">
            {sender ? (
              <ChatPersonCard
                person={sender}
                variant="inline"
                side="bottom"
                className="text-sm font-semibold text-text"
                detail={roleText(sender.role) + (sender.extension ? ` · ext. ${sender.extension}` : "")}
                canMessage={messageable(sender, me, { kind: chatKind ?? "team" })}
                onOpenChat={onOpenChat}
              >
                {senderName}
              </ChatPersonCard>
            ) : (
              <span className="text-sm font-semibold text-text">{senderName}</span>
            )}
            {m.sender_role && <span className="text-2xs text-text-subtle">{roleText(m.sender_role)}</span>}
            <time className="text-2xs text-text-subtle" dateTime={m.created_at}>
              {at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
            </time>
          </p>
        )}
        {m.handoff ? (
          <div className="mt-1">
            <CloserAskCard ask={m.handoff} contact={m.contact ? <ContactCard contact={m.contact} /> : null} />
          </div>
        ) : (
          <>
            {m.body && (
              <p className="whitespace-pre-wrap break-words text-sm text-text">
                {mentionParts(m.body, m.mentions ?? []).map((part, i) =>
                  part.mention ? (
                    <span
                      key={i}
                      className={cn(
                        "rounded px-0.5 font-medium",
                        part.mention.user_id === me ? "bg-accent-4 text-accent-12" : "text-accent-11",
                      )}
                    >
                      {part.text}
                    </span>
                  ) : (
                    <React.Fragment key={i}>{part.text}</React.Fragment>
                  ),
                )}
              </p>
            )}
            {m.contact && <ContactCard contact={m.contact} />}
          </>
        )}
        {reactions.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {reactions.map((r) => {
              const who = `${reactionWho(r, myName)} reacted with ${r.emoji}`;
              // Who reacted, the moment the pointer is on the chip, as in Slack: the emoji large, the
              // names under it. A read-only chip is aria-disabled, not disabled: a disabled button
              // gets no pointer events, so its tooltip would never open. The names are said once, by
              // the tooltip, which describes the chip; the chip's own label is the emoji and count.
              return (
                <Tooltip
                  key={r.emoji}
                  label={who}
                  content={
                    <span className="flex max-w-56 flex-col items-center gap-1 py-1 text-center">
                      <span className="text-2xl leading-none" aria-hidden>{r.emoji}</span>
                      <span>{who}</span>
                    </span>
                  }
                >
                  <button
                    type="button"
                    aria-pressed={r.mine}
                    aria-label={`${r.emoji} ${r.count}`}
                    aria-disabled={readOnly || undefined}
                    onClick={() => !readOnly && toggle(r.emoji, !r.mine)}
                    className={cn(
                      "inline-flex h-6 items-center gap-1 rounded-full border px-2 text-xs tabular transition-colors",
                      "aria-disabled:cursor-default",
                      r.mine
                        ? "border-accent-7 bg-accent-3 font-medium text-accent-12"
                        : "border-border bg-surface text-text-muted",
                      // A chip that changes nothing when pressed does not look pressable.
                      !readOnly && !r.mine && "hover:border-gray-7 hover:text-text",
                    )}
                  >
                    <span className="text-sm leading-none" aria-hidden>{r.emoji}</span>
                    {r.count}
                  </button>
                </Tooltip>
              );
            })}
            {!readOnly && (
              <ReactionPicker onPick={(emoji) => toggle(emoji)}>
                <button
                  type="button"
                  aria-label="Add a reaction"
                  title="Add a reaction"
                  className={cn(
                    "inline-flex h-6 items-center rounded-full border border-dashed border-border px-1.5 text-text-muted",
                    "opacity-0 transition-opacity hover:border-gray-7 hover:text-text group-hover:opacity-100",
                    "focus-visible:opacity-100 data-[state=open]:opacity-100",
                  )}
                >
                  <SmilePlus className="size-3.5" aria-hidden />
                </button>
              </ReactionPicker>
            )}
          </div>
        )}
        {threadable && m.thread && m.thread.reply_count > 0 && (
          <button
            type="button"
            onClick={() => onOpenThread(m.id)}
            className="mt-1 flex items-center gap-2 rounded-md py-0.5 pr-1.5 text-xs hover:bg-surface-hover"
          >
            <span className="flex -space-x-1.5">
              {m.thread.repliers.map((p) => (
                <PictureAvatar key={p.user_id} name={p.name} url={p.avatar_url} className="size-5 ring-2 ring-surface-sunken" />
              ))}
            </span>
            <span className="font-medium text-accent-11">{threadLine(m.thread)}</span>
            {m.thread.unread > 0 && (
              <span className="rounded bg-accent-9 px-1 text-2xs font-semibold text-white">{m.thread.unread} new</span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

/** The emoji to react with (platform ADR-186, "Reactions"): the quick ones in a row, then the rest.
 *  A pick closes it. Opened from a message's hover bar, or from the "+" after its reactions. */
function ReactionPicker({
  children, onPick, open, onOpenChange,
}: {
  /** What opens it: a button. */
  children: React.ReactNode;
  onPick: (emoji: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [inner, setInner] = React.useState(false);
  const shown = open ?? inner;
  const setShown = (next: boolean) => {
    setInner(next);
    onOpenChange?.(next);
  };
  const pick = (emoji: string) => {
    onPick(emoji);
    setShown(false);
  };
  const emojiButton = (emoji: string) => (
    <button
      key={emoji}
      type="button"
      aria-label={`React with ${emoji}`}
      onClick={() => pick(emoji)}
      className="flex size-8 items-center justify-center rounded-md text-lg leading-none hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8"
    >
      {emoji}
    </button>
  );
  return (
    <Popover open={shown} onOpenChange={setShown}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent side="top" align="end" aria-label="Add a reaction" className="w-auto p-1.5">
        <div className="grid grid-cols-8 gap-0.5 border-b border-border pb-1.5">{QUICK_REACTIONS.map(emojiButton)}</div>
        <div className="grid grid-cols-8 gap-0.5 pt-1.5">{MORE_REACTIONS.map(emojiButton)}</div>
      </PopoverContent>
    </Popover>
  );
}

function ContactCard({ contact }: { contact: SharedContact }) {
  if (!contact.may_open) {
    return (
      <div className="mt-1 flex max-w-md items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2 text-xs text-text-muted">
        <Lock className="size-3.5 shrink-0" aria-hidden />A contact you cannot open
      </div>
    );
  }
  const stage = contact.stage ? (catalogRow(contact.stage)?.label ?? contact.stage) : null;
  return (
    <div className="mt-1 flex max-w-md items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2">
      <Avatar name={contact.name ?? "?"} className="size-7" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text">{contact.name ?? "A contact with no name"}</p>
        <p className="truncate text-xs text-text-muted">
          {[stage, contact.phone_last4 ? `ends ${contact.phone_last4}` : null].filter(Boolean).join(" · ")}
        </p>
      </div>
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/contacts/${contact.lead_id}`}>
          <ExternalLink />
          Open
        </Link>
      </Button>
    </div>
  );
}

export function Composer({
  chatId, placeholder, threadId,
}: {
  chatId: number;
  placeholder: string;
  /** A reply in the thread of this message (platform ADR-191). */
  threadId?: number;
}) {
  const { user } = useAuth();
  const me = user?.id ?? 0;
  const toast = useToast();
  const send = useSendChatMessage();
  const detail = useChat(chatId);
  const [body, setBody] = React.useState("");
  const [sharing, setSharing] = React.useState(false);
  // A contact picked to share goes with the next message, under whatever is written with it.
  const [attached, setAttached] = React.useState<ContactRow | null>(null);
  const box = React.useRef<HTMLTextAreaElement>(null);
  // Mentions (platform ADR-186): the "@..." being typed, the person lit in the list, and who was
  // picked; the ones whose "@Name" is still in the words go with the message.
  const [typing, setTyping] = React.useState<{ start: number; query: string } | null>(null);
  const [active, setActive] = React.useState(0);
  const [picked, setPicked] = React.useState<Mention[]>([]);
  const listId = React.useId();
  const chat = detail.data;
  const mentions = chat !== undefined && chat.kind !== "direct";
  const matches = typing && chat && mentions ? mentionMatches(chat.members, typing.query, me) : [];
  const picking = matches.length > 0;
  const current = Math.min(active, Math.max(0, matches.length - 1));

  const look = (el: HTMLTextAreaElement) => {
    setTyping(mentionQuery(el.value, el.selectionStart ?? el.value.length));
    setActive(0);
  };

  const pick = (m: Mention) => {
    const el = box.current;
    if (!el || !typing) return;
    const next = insertMention(body, typing.start, el.selectionStart ?? body.length, m.name);
    setBody(next.text);
    setPicked((was) => [...was.filter((p) => p.user_id !== m.user_id), m]);
    setTyping(null);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.caret, next.caret);
    });
  };

  const submit = async () => {
    const text = body.trim();
    if (!text && !attached) return;
    try {
      await send.mutateAsync({
        chatId, body: text, leadId: attached?.id, clientId: newClientId(), mentionIds: mentionIdsIn(text, picked), threadId,
      });
      setBody("");
      setAttached(null);
      setPicked([]);
      setTyping(null);
    } catch (e) {
      toast.error("Not sent", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <div className="border-t border-border bg-surface p-3">
      <div className="relative rounded-lg border border-border bg-surface">
        {picking && (
          <ul
            id={listId}
            role="listbox"
            aria-label="People to mention"
            className="absolute bottom-full left-0 z-10 mb-1 w-64 overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-lg"
          >
            {matches.map((m, i) => {
              const person = chat?.members.find((p) => p.user_id === m.user_id);
              return (
                <li
                  key={m.user_id}
                  id={`${listId}-${m.user_id}`}
                  role="option"
                  aria-selected={i === current}
                  // Keep the caret in the box: the press picks, it does not blur.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(m)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-text",
                    i === current && "bg-accent-3",
                  )}
                >
                  <PictureAvatar name={m.name} url={person?.avatar_url} className="size-6" />
                  <span className="min-w-0 flex-1 truncate">{m.name}</span>
                  {person?.guest && <span className="text-2xs text-text-subtle">guest</span>}
                </li>
              );
            })}
          </ul>
        )}
        {attached && (
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Contact className="size-4 shrink-0 text-accent-11" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-sm text-text">
              {[attached.first_name, attached.last_name].filter(Boolean).join(" ") || attached.phone}
              {attached.stage && (
                <span className="text-xs text-text-muted"> · {catalogRow(attached.stage)?.label ?? attached.stage}</span>
              )}
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Remove the contact"
              title="Remove the contact"
              onClick={() => setAttached(null)}
            >
              <X />
            </Button>
          </div>
        )}
        <Textarea
          ref={box}
          rows={2}
          value={body}
          placeholder={attached ? "Add a message, or just send the contact" : placeholder}
          aria-label={placeholder}
          aria-autocomplete={mentions ? "list" : undefined}
          aria-controls={picking ? listId : undefined}
          aria-activedescendant={picking ? `${listId}-${matches[current]?.user_id}` : undefined}
          // The chat window beside the rail leaves Escape to the list while it is open.
          data-mention-picking={picking ? "" : undefined}
          maxLength={4000}
          className="resize-none border-0 shadow-none focus-visible:ring-0"
          onChange={(e) => {
            setBody(e.target.value);
            look(e.target);
          }}
          onClick={(e) => look(e.currentTarget)}
          onBlur={() => setTyping(null)}
          onKeyDown={(e) => {
            if (picking && !e.nativeEvent.isComposing) {
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const step = e.key === "ArrowDown" ? 1 : -1;
                setActive((current + step + matches.length) % matches.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                const m = matches[current];
                if (m) pick(m);
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setTyping(null);
                return;
              }
            }
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void submit();
            }
          }}
          onKeyUp={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Home" || e.key === "End") look(e.currentTarget);
          }}
        />
        <div className="flex items-center gap-1 px-2 pb-2">
          <Button variant="ghost" size="icon-sm" aria-label="Share a contact" title="Share a contact" onClick={() => setSharing(true)}>
            <UserRound />
          </Button>
          <span className="flex-1" />
          <span className="hidden pr-2 text-2xs text-text-subtle sm:inline">
            {mentions ? "@ to mention · " : ""}Enter to send, Shift+Enter for a new line
          </span>
          <Button
            variant="default"
            size="sm"
            onClick={() => void submit()}
            loading={send.isPending}
            disabled={!body.trim() && !attached}
          >
            <Send />
            Send
          </Button>
        </div>
      </div>
      <ShareContactDialog
        open={sharing}
        onOpenChange={setSharing}
        onPick={(lead) => {
          setAttached(lead);
          setSharing(false);
        }}
        // Back to the words, to write what goes with the contact.
        onClosed={() => box.current?.focus()}
      />
    </div>
  );
}

/** The chat's people beside it, shown and hidden from the people button in the chat's head. */
function PeoplePanel({ chat, onHide }: { chat: ChatDetail; onHide: () => void }) {
  const { user } = useAuth();
  const me = user?.id ?? 0;
  return (
    <aside className="hidden w-64 shrink-0 flex-col border-l border-border bg-surface lg:flex" aria-label="People">
      <div className="flex items-center gap-2 border-b border-border py-2 pl-4 pr-2">
        <p className="flex-1 text-sm font-semibold text-text">{chat.kind === "team" ? "People" : "Details"}</p>
        <Button variant="ghost" size="icon-sm" aria-label="Hide the people" onClick={onHide}>
          <X />
        </Button>
      </div>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3 scrollbar-subtle">
        {chat.members.map((m) => {
          const detail =
            roleText(m.role) +
            (m.extension ? ` · ext. ${m.extension}` : "") +
            (m.guest ? " · guest" : "") +
            (!m.is_active ? " · deactivated" : "");
          return (
            <ChatPersonCard key={m.user_id} person={m} detail={detail} canMessage={messageable(m, me, chat)}>
              <PictureAvatar name={m.name} url={m.avatar_url} className="size-7" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-text">
                  {m.name}
                  {m.user_id === me ? " (you)" : ""}
                </span>
                <span className="block truncate text-xs text-text-muted">{detail}</span>
              </span>
            </ChatPersonCard>
          );
        })}
      </div>
      {chat.kind === "team" && chat.team_id !== null && (
        <p className="border-t border-border p-3 text-xs text-text-muted">
          The team's people and its leads are in this chat
          {chat.members.some((m) => m.guest)
            ? ", and guests who joined it from outside the team: they read and write here, and take no ask"
            : ""}
          . Admins read and write every team's chat without being in it. Who is in the team is changed on{" "}
          <Link to={`/teams/${chat.team_id}`} className="text-accent-11 hover:underline">
            the team's page
          </Link>
          .
        </p>
      )}
      {chat.kind === "contact" && (
        <p className="border-t border-border p-3 text-xs text-text-muted">
          The setter and the closer of each handoff for this contact are in this conversation. Anyone who may open the
          contact reads it on{" "}
          {chat.contact?.may_open ? (
            <Link to={`/contacts/${chat.contact.lead_id}`} className="text-accent-11 hover:underline">
              their record
            </Link>
          ) : (
            "their record"
          )}
          , under the call each handoff came from.
        </p>
      )}
    </aside>
  );
}

// --- starting a chat, sharing a contact ----------------------------------------------------------

function NewMessageDialog({
  open, onOpenChange, onOpened,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpened: (chat: ChatSummary) => void;
}) {
  const toast = useToast();
  const [q, setQ] = React.useState("");
  const typed = useDebounced(q.trim(), 200);
  const people = useChatPeople(typed, open);
  const start = useOpenDirectChat();
  const teams = useOpenTeamChats(open);
  const join = useJoinTeamChat();
  const openTeams = (teams.data ?? []).filter((t) => t.name.toLowerCase().includes(typed.toLowerCase()));

  React.useEffect(() => {
    if (open) setQ("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
          <DialogDescription>
            Message anyone in your organization, or a team that is open to everyone. Your teams' chats are already on
            the left.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Input
            autoFocus
            leading={<Search className="size-4" />}
            placeholder="Search by name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search people"
          />
          {openTeams.length > 0 && (
            <div>
              <p className="pb-1.5 text-2xs font-medium uppercase tracking-wide text-text-subtle">Teams open to everyone</p>
              <ul className="divide-y divide-border rounded-lg border border-border">
                {openTeams.map((t) => (
                  <li key={t.team_id}>
                    <button
                      type="button"
                      disabled={join.isPending}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-hover"
                      onClick={async () => {
                        try {
                          const chat = await join.mutateAsync(t.team_id);
                          onOpenChange(false);
                          onOpened(chat);
                        } catch (e) {
                          toast.error("Could not open the team's chat", e instanceof ApiError ? e.message : "Please try again.");
                        }
                      }}
                    >
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-gray-3 text-text-muted">
                        <Hash className="size-4" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-text">{t.name}</span>
                        <span className="block truncate text-xs text-text-muted">
                          {t.people} {t.people === 1 ? "person" : "people"}
                          {t.joined ? " · you are in its chat" : " · join as a guest"}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="pb-1.5 pt-3 text-2xs font-medium uppercase tracking-wide text-text-subtle">People</p>
            </div>
          )}
          {people.error && <Callout tone="danger">Could not load the people.</Callout>}
          <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border scrollbar-subtle">
            {people.isPending && <li className="p-3 text-xs text-text-muted">Loading…</li>}
            {people.data?.length === 0 && <li className="p-3 text-xs text-text-muted">Nobody matches.</li>}
            {people.data?.map((p) => (
              <li key={p.user_id}>
                <button
                  type="button"
                  disabled={start.isPending}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-hover"
                  onClick={async () => {
                    try {
                      const chat = await start.mutateAsync(p.user_id);
                      onOpenChange(false);
                      onOpened(chat);
                    } catch (e) {
                      toast.error("Could not open the chat", e instanceof ApiError ? e.message : "Please try again.");
                    }
                  }}
                >
                  <Avatar name={p.name ?? "?"} className="size-7" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-text">{p.name}</span>
                    <span className="block truncate text-xs text-text-muted">
                      {roleText(p.role)}
                      {p.extension ? ` · ext. ${p.extension}` : ""}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function ShareContactDialog({
  open, onOpenChange, onPick, onClosed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (lead: ContactRow) => void;
  /** Where the focus goes as it closes, instead of the button that opened it. */
  onClosed?: () => void;
}) {
  const [q, setQ] = React.useState("");
  const typed = useDebounced(q.trim(), 250);
  const contacts = useContacts({ q: typed || undefined }, 0, open && typed.length >= 2);

  React.useEffect(() => {
    if (open) setQ("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onCloseAutoFocus={(e) => {
          if (!onClosed) return;
          e.preventDefault();
          onClosed();
        }}
      >
        <DialogHeader>
          <DialogTitle>Share a contact</DialogTitle>
          <DialogDescription>
            They go with your message as a card that opens their record, for whoever in the chat may open it.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Input
            autoFocus
            leading={<Search className="size-4" />}
            placeholder="Name or phone number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search contacts"
          />
          <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border scrollbar-subtle">
            {typed.length < 2 && <li className="p-3 text-xs text-text-muted">Type at least two letters or digits.</li>}
            {typed.length >= 2 && contacts.isPending && <li className="p-3 text-xs text-text-muted">Searching…</li>}
            {typed.length >= 2 && contacts.data?.items.length === 0 && (
              <li className="p-3 text-xs text-text-muted">No contact you can open matches.</li>
            )}
            {typed.length >= 2 &&
              contacts.data?.items.map((c) => {
                const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.phone;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-hover"
                      onClick={() => onPick(c)}
                    >
                      <Avatar name={name} className="size-7" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-text">{name}</span>
                        <span className="block truncate text-xs text-text-muted">
                          {[c.stage ? (catalogRow(c.stage)?.label ?? c.stage) : null, c.firm?.label ?? null].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
